import { Box, Button, TextField, Typography, Divider, Alert } from "@mui/material";
import { useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SelectorClient from "../service_order/selectorClient";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { httpClient } from "../../common/httpclient";
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import NotesIcon from '@mui/icons-material/Notes';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Stack } from '@mui/material';
import TextAreaWithCounter from '../../component/input/TextAreaWithCounter';
import { standaloneFieldSx } from '../../common/fieldStyles';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PedalBikeIcon from '@mui/icons-material/PedalBike';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import { useEnterpriseConfigContext } from '../../component/context/EnterpriseConfigContext';
import ArticleIcon from '@mui/icons-material/Article';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/pt-br';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import Build from '@mui/icons-material/Build';
import Speed from '@mui/icons-material/Speed';

dayjs.extend(utc);

const QuoteData = forwardRef(({ 
    isForm, 
    editMode, 
    handleSubmit, 
    id, 
    setQuoteNumber, 
    wasUpdated, 
    setQuoteStatus, 
    onValidationChange,
    showActionButtons = false,
    onSave,
    isFormValid = false,
    isSaving = false,
    status,
    onStatusChange,
}, ref) => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [edit, setEdit] = useState(isForm);
    const [quote, setQuote] = useState(null);
    const [currentStatus, setCurrentStatus] = useState(status || 'Pendente');
    const storage = localStorage;
    const [useVehicle, setUseVehicle] = useState(false);
    const { settings, isLoading: configLoading } = useEnterpriseConfigContext();
    const validationSchema = useMemo(() => {
        return Yup.object().shape({
            customer_id: Yup.string().required('Cliente é obrigatório'),
           
            observations: Yup.string().optional(),
            defect_reason: Yup.string().optional()
        });
    }, [settings?.os_use_vehicle]);

    const formik = useFormik({
        initialValues: {
            customer_id: '',
            order_date: null,
            observations: '',
            defect_reason: '',
            vehicle_id: ''
        },
        validateOnMount: false,
        validateOnChange: true,
        validateOnBlur: true,
        validationSchema: validationSchema,
        onSubmit: (values) => {           
            const formatDate = (dateValue) => {
                if (!dateValue) return '';
                const date = dateValue && typeof dateValue.isValid === 'function' ? dateValue : dayjs.utc(dateValue);
                if (!date || !date.isValid || !date.isValid()) return '';
                const utcDate = date.utc();
                return utcDate.format('YYYY-MM-DDTHH:mm');
            };

            const formattedValues = {
                ...values,
                order_date: formatDate(values.order_date),
                delivery_date: formatDate(values.delivery_date)
            };
            
            handleSubmit(formattedValues);
        },
        enableReinitialize: true
    });

    useEffect(() => {
        if (settings && settings.os_use_vehicle) {
            setUseVehicle(true);
        } else if (settings && !settings.os_use_vehicle) {
            setUseVehicle(false);
        }
    }, [settings]);

    useEffect(() => {
        if (isForm) {
            setEdit(true);
        }
        if (wasUpdated) {
            fetchQuoteData();
        }
    }, [isForm, wasUpdated]);

    useEffect(() => {
        if (formik && settings) {
            formik.validateForm();
        }
    }, [settings?.os_use_vehicle]);

    useEffect(() => {
        if (onValidationChange) {
            const hasCustomer = formik.values.customer_id && formik.values.customer_id.toString().trim() !== '';
            const orderDate = formik.values.order_date;
            const hasOrderDate = orderDate && (orderDate.isValid ? orderDate.isValid() : dayjs(orderDate).isValid());
            const deliveryDate = formik.values.delivery_date;
            const hasDeliveryDate = deliveryDate && (deliveryDate.isValid ? deliveryDate.isValid() : dayjs(deliveryDate).isValid());
            
            const hasAllRequiredFields = hasCustomer && hasOrderDate && hasDeliveryDate;
            const hasNoErrors = Object.keys(formik.errors).length === 0;
            const hasTouchedFields = Object.keys(formik.touched).length > 0;
            
            const isValid = hasAllRequiredFields && hasNoErrors && hasTouchedFields;
            
            onValidationChange(isValid);
        }
    }, [formik.values, formik.errors, formik.touched, useVehicle, onValidationChange]);

    const fetchQuoteData = async () => {
        try {
            const response = await httpClient.get(`/servicequotes/${id}`);
 
            if (response.status === 200) {
                setQuote(response.data);
                if (setQuoteNumber) {
                    setQuoteNumber(response.data?.number);
                }
                
                const newStatus = response.data?.status || 'Pendente';
                
                if (newStatus !== currentStatus) {
                    setCurrentStatus(newStatus);
                    if (setQuoteStatus) {
                        setQuoteStatus(newStatus);
                    }
                    
                    if (onStatusChange) {
                        onStatusChange(newStatus);
                    }
                }
                
                setSelectedCustomer(response.data?.customer_id || '');
                setSelectedVehicle(response.data?.vehicle_id || '');
                formik.setValues({
                    customer_id: response.data?.customer_id || '',
                    order_date: response.data?.order_date ? dayjs.utc(response.data.order_date) : null,
                    delivery_date: response.data?.delivery_date ? dayjs.utc(response.data.delivery_date) : null,
                    observations: response.data?.observations || '',
                    defect_reason: response.data?.defect_reason || '',
                    vehicle_id: response.data?.vehicle_id || ''
                });
            }
        } catch (error) {
            console.error('Error fetching quote data:', error);
        }
    };

    useEffect(() => {
        if (id) {
            fetchQuoteData();            
        }
    }, [id]);

    useEffect(() => {
        if (status && status !== currentStatus) {
            setCurrentStatus(status);
        }
    }, [status]);

    useImperativeHandle(ref, () => ({
        submitForm: () => {
            formik.validateForm().then((errors) => {
                if (Object.keys(errors).length === 0) {
                    formik.handleSubmit();
                } else {
                    const touchedFields = {};
                    Object.keys(formik.values).forEach(key => {
                        touchedFields[key] = true;
                    });
                    formik.setTouched(touchedFields);
                }
            });
        },
        getCurrentStatus: () => currentStatus,
        getQuoteData: () => quote,
        updateStatus: (newStatus) => {
            if (newStatus && newStatus !== currentStatus) {
                setCurrentStatus(newStatus);
                if (setQuoteStatus) {
                    setQuoteStatus(newStatus);
                }
                if (onStatusChange) {
                    onStatusChange(newStatus);
                }
            }
        }
    }));

    const handleCustomerSelect = (customerId) => {
        formik.setFieldValue('customer_id', customerId);
        formik.setFieldTouched('customer_id', true);
        setSelectedCustomer(customerId);
        
        setTimeout(() => {
            formik.validateField('customer_id');
        }, 100);
    };

    const handleVehicleSelect = (event) => {
        const vehicleId = event.target.value;
        formik.setFieldValue('vehicle_id', vehicleId);
        formik.setFieldTouched('vehicle_id', true);
        setSelectedVehicle(vehicleId);
    };

    if (configLoading) {
        return (
            <Box sx={{ width: '100%', p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Carregando configurações...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {isForm ? (
                <form onSubmit={formik.handleSubmit}>
                    <Box sx={{ 
                        p: 2, 
                        backgroundColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255,255,255,0.02)' 
                            : 'grey.50' 
                    }}>
                       
                        
                    </Box>

                    <Divider />

                    <Box sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                            <PersonIcon color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {useVehicle ? 'Cliente e Veículo' : 'Cliente'}
                            </Typography>
                        </Stack>
                        
                        <SelectorClient
                            formik={formik}
                            selectedCustomer={selectedCustomer}
                            handleChange={handleCustomerSelect}
                            selectedVehicle={selectedVehicle}
                            handleVehicleChange={handleVehicleSelect}
                            useVehicle={useVehicle}
                        />
                    </Box>

                    <Divider />

                    <Box sx={{ 
                        p: 2, 
                        backgroundColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255,255,255,0.02)' 
                            : 'grey.50' 
                    }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <NotesIcon color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Observações
                            </Typography>
                        </Stack>
                        
                        <Stack spacing={2}>
                        <TextAreaWithCounter
                            label="Motivo/Defeito (opcional)"
                            maxLength={500}
                            name="defect_reason"
                            value={formik.values.defect_reason}
                            handleChange={formik.handleChange}
                            error={formik.touched.defect_reason && Boolean(formik.errors.defect_reason)}
                            helperText={formik.touched.defect_reason && formik.errors.defect_reason}
                            variant="outlined"
                            rows={3}
                            sx={standaloneFieldSx}
                            captionText="Descreva o motivo da entrada ou o defeito relatado"
                        />
                        <TextAreaWithCounter
                            label="Observações sobre o orçamento (opcional)"
                            maxLength={500}
                            name="observations"
                            value={formik.values.observations}
                            handleChange={formik.handleChange}
                            error={formik.touched.observations && Boolean(formik.errors.observations)}
                            helperText={formik.touched.observations && formik.errors.observations}
                            variant="outlined"
                            rows={3}
                            sx={standaloneFieldSx}
                        />
                        </Stack>
                    </Box>

                    {!formik.isValid && formik.dirty && (
                        <Box sx={{ p: 2 }}>
                            <Alert 
                                severity="warning" 
                                sx={{ borderRadius: 2 }}
                                icon={<CheckCircleIcon />}
                            >
                                <Typography variant="body2">
                                    Por favor, corrija os erros no formulário antes de continuar.
                                </Typography>
                            </Alert>
                        </Box>
                    )}

                    {showActionButtons && (
                        <Box sx={{ 
                            p: 2, 
                            backgroundColor: theme.palette.background.paper,
                            borderTop: '1px solid',
                            borderColor: theme.palette.divider
                        }}>
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={() => navigate('/servicequotes')}
                                    disabled={isSaving}
                                    sx={{ 
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: theme.palette.mode === 'dark' 
                                            ? 'rgba(255,255,255,0.2)' 
                                            : 'rgba(0,0,0,0.1)',
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            borderColor: theme.palette.primary.main,
                                            backgroundColor: theme.palette.mode === 'dark' 
                                                ? 'rgba(25, 118, 210, 0.08)' 
                                                : 'rgba(25, 118, 210, 0.04)',
                                        }
                                    }}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={onSave}
                                    disabled={isSaving}
                                    sx={{ 
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        backgroundColor: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark
                                        }
                                    }}
                                >
                                    {isSaving ? 'Gerando Orçamento...' : 'Gerar'}
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </form>
            ) : (
                <Box sx={{ 
                    p: 2,
                    background: theme.palette.mode === 'dark' 
                        ? 'linear-gradient(135deg, rgba(18, 18, 18, 0.8) 0%, rgba(25, 25, 25, 0.9) 100%)'
                        : 'linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.9) 100%)',
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                    boxShadow: theme.palette.mode === 'dark' 
                        ? '0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)'
                        : '0 8px 32px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.05)'
                }}>
                    <Box sx={{ 
                        display: 'grid',
                        gridTemplateColumns: { 
                            xs: '1fr', 
                            sm: quote?.vehicle_id ? 'repeat(2, 1fr)' : '1fr', 
                            lg: quote?.vehicle_id ? 'repeat(2, 1fr)' : '1fr' 
                        },
                        gap: 2,
                        mb: 2
                    }}>
                        <Paper sx={{ 
                            p: 2,
                            background: theme.palette.mode === 'dark' 
                                ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(30, 136, 229, 0.12) 100%)'
                                : 'linear-gradient(135deg, rgba(33, 150, 243, 0.03) 0%, rgba(30, 136, 229, 0.06) 100%)',
                            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.1)'}`,
                            borderRadius: 2,
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <Box sx={{ 
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                width: 60,
                                height: 60,
                                background: theme.palette.mode === 'dark' 
                                    ? 'radial-gradient(circle, rgba(33, 150, 243, 0.1) 0%, transparent 70%)'
                                    : 'radial-gradient(circle, rgba(33, 150, 243, 0.05) 0%, transparent 70%)',
                                borderRadius: '50%'
                            }} />
                            
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                                <Box sx={{ 
                                    p: 1,
                                    borderRadius: 1.5,
                                    background: theme.palette.mode === 'dark' 
                                        ? 'rgba(33, 150, 243, 0.2)'
                                        : 'rgba(33, 150, 243, 0.1)'
                                }}>
                                    <ArticleIcon sx={{ fontSize: 18, color: 'info.main' }} />
                                </Box>
                                <Typography variant="subtitle1" sx={{ 
                                    fontWeight: 600,
                                    color: 'text.primary'
                                }}>
                                    Detalhes do Orçamento
                                </Typography>
                            </Stack>
                            
                            <Stack spacing={1.5}>
                                <Box sx={{ 
                                    display: 'grid',
                                    gridTemplateColumns: { 
                                        xs: '1fr', 
                                        sm: quote?.vehicle_id ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
                                        lg: quote?.vehicle_id ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)'
                                    },
                                    gap: 3,
                                    mb: quote?.vehicle_id ? 0 : 2
                                }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ 
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            mb: 0.5,
                                            display: 'block'
                                        }}>
                                            Data do Orçamento
                                        </Typography>
                                        <Typography variant="body1" sx={{ 
                                            fontWeight: 500,
                                            color: 'text.primary'
                                        }}>
                                            {quote?.order_date && new Date(quote.order_date).toLocaleString('pt-BR', { 
                                                timeZone: 'UTC',
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </Typography>
                                    </Box>
                                    
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ 
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            mb: 0.5,
                                            display: 'block'
                                        }}>
                                            Data de Entrega
                                        </Typography>
                                        <Typography variant="body1" sx={{ 
                                            fontWeight: 500,
                                            color: 'text.primary'
                                        }}>
                                            {quote?.delivery_date && new Date(quote.delivery_date).toLocaleString('pt-BR', { 
                                                timeZone: 'UTC',
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ 
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                        mb: 0.5,
                                        display: 'block'
                                    }}>
                                        Cliente
                                    </Typography>
                                    <Typography variant="body1" sx={{ 
                                        fontWeight: 500,
                                        color: 'text.primary'
                                    }}>
                                        {quote?.customer_name}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        {quote?.vehicle_id && (
                            <Paper sx={{ 
                                p: 2,
                                background: theme.palette.mode === 'dark' 
                                    ? 'linear-gradient(135deg, rgba(156, 39, 176, 0.08) 0%, rgba(142, 36, 170, 0.12) 100%)'
                                    : 'linear-gradient(135deg, rgba(156, 39, 176, 0.03) 0%, rgba(142, 36, 170, 0.06) 100%)',
                                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)'}`,
                                borderRadius: 2,
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <Box sx={{ 
                                    position: 'absolute',
                                    top: -10,
                                    right: -10,
                                    width: 60,
                                    height: 60,
                                    background: theme.palette.mode === 'dark' 
                                        ? 'radial-gradient(circle, rgba(156, 39, 176, 0.1) 0%, transparent 70%)'
                                        : 'radial-gradient(circle, rgba(156, 39, 176, 0.05) 0%, transparent 70%)',
                                    borderRadius: '50%'
                                }} />
                                
                                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                                    <Box sx={{ 
                                        p: 1,
                                        borderRadius: 1.5,
                                        background: theme.palette.mode === 'dark' 
                                            ? 'rgba(156, 39, 176, 0.2)'
                                            : 'rgba(156, 39, 176, 0.1)'
                                    }}>
                                        {quote?.vehicle_type === 'bicycle' ? <PedalBikeIcon sx={{ fontSize: 18, color: 'secondary.main' }} /> :
                                         quote?.vehicle_type === 'motorcycle' ? <TwoWheelerIcon sx={{ fontSize: 18, color: 'secondary.main' }} /> :
                                         <DirectionsCarIcon sx={{ fontSize: 18, color: 'secondary.main' }} />}
                                    </Box>
                                    <Typography variant="subtitle1" sx={{ 
                                        fontWeight: 600,
                                        color: 'text.primary'
                                    }}>
                                        Veículo
                                    </Typography>
                                </Stack>
                                
                                <Stack spacing={1.5}>
                                    <Typography variant="body1" sx={{ 
                                        fontWeight: 500,
                                        color: 'text.primary',
                                        fontSize: '1.1rem'
                                    }}>
                                        {quote?.vehicle_name || '-'}
                                    </Typography>
                                    
                                    {quote?.vehicle_type !== 'bicycle' && quote?.vehicle_plate && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1,
                                            p: 1.5,
                                            borderRadius: 1,
                                            background: theme.palette.mode === 'dark' 
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'rgba(0,0,0,0.02)'
                                        }}>
                                            <ConfirmationNumber sx={{ fontSize: 16, color: 'success.main' }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {quote.vehicle_plate}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {quote?.vehicle_chassi && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1,
                                            p: 1.5,
                                            borderRadius: 1,
                                            background: theme.palette.mode === 'dark' 
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'rgba(0,0,0,0.02)'
                                        }}>
                                            <Build sx={{ fontSize: 16, color: 'info.main' }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {quote.vehicle_chassi}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {quote?.vehicle_kilometer && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1,
                                            p: 1.5,
                                            borderRadius: 1,
                                            background: theme.palette.mode === 'dark' 
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'rgba(0,0,0,0.02)'
                                        }}>
                                            <Speed sx={{ fontSize: 16, color: 'warning.main' }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {quote.vehicle_kilometer} km
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </Paper>
                        )}
                    </Box>

                    {(quote?.observations || quote?.defect_reason) && (
                        <Paper sx={{ 
                            p: 2,
                            background: theme.palette.mode === 'dark' 
                                ? 'linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(245, 124, 0, 0.12) 100%)'
                                : 'linear-gradient(135deg, rgba(255, 152, 0, 0.03) 0%, rgba(245, 124, 0, 0.06) 100%)',
                            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)'}`,
                            borderRadius: 2,
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <Box sx={{ 
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                width: 60,
                                height: 60,
                                background: theme.palette.mode === 'dark' 
                                    ? 'radial-gradient(circle, rgba(255, 152, 0, 0.1) 0%, transparent 70%)'
                                    : 'radial-gradient(circle, rgba(255, 152, 0, 0.05) 0%, transparent 70%)',
                                borderRadius: '50%'
                            }} />
                            
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                                <Box sx={{ 
                                    p: 1,
                                    borderRadius: 1.5,
                                    background: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 152, 0, 0.2)'
                                        : 'rgba(255, 152, 0, 0.1)'
                                }}>
                                    <NotesIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                                </Box>
                                <Typography variant="subtitle1" sx={{ 
                                    fontWeight: 600,
                                    color: 'text.primary'
                                }}>
                                    Observações
                                </Typography>
                            </Stack>

                            {quote.defect_reason && (
                                <Box sx={{ mb: quote.observations ? 2 : 0 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
                                        Motivo/Defeito
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        fontWeight: 400,
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.6,
                                        color: 'text.primary',
                                        p: 2,
                                        borderRadius: 1,
                                        background: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.05)'
                                            : 'rgba(0,0,0,0.02)',
                                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`
                                    }}>
                                        {quote.defect_reason}
                                    </Typography>
                                </Box>
                            )}

                            {quote.observations && (
                                <Box>
                                    {quote.defect_reason && (
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
                                            Observações
                                        </Typography>
                                    )}
                                    <Typography variant="body1" sx={{
                                        fontWeight: 400,
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.6,
                                        color: 'text.primary',
                                        p: 2,
                                        borderRadius: 1,
                                        background: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.05)'
                                            : 'rgba(0,0,0,0.02)',
                                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`
                                    }}>
                                        {quote.observations}
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    )}
                </Box>
            )}
        </Box>
    );
});

export default QuoteData;

